// French (default) and English. The language follows the user's profile once signed in, else ?lang= (the
// public site's links carry it), else the last choice, else the browser's; en.ts is the reference, and fr.ts
// must have the same keys (a type error otherwise).

import i18next from 'i18next';
import { initReactI18next, useTranslation } from 'react-i18next';
import type { Lang } from '../lib/format.ts';
import { en } from './en.ts';
import { fr } from './fr.ts';

declare module 'i18next' {
  interface CustomTypeOptions {
    defaultNS: 'translation';
    resources: { translation: typeof en };
  }
}

const initial: Lang = (() => {
  const asked = new URLSearchParams(location.search).get('lang');
  if (asked === 'fr' || asked === 'en') {
    try {
      localStorage.setItem('cck.lang', asked);
    } catch {
      // storage unavailable: the choice holds for this visit
    }
    return asked;
  }
  try {
    const saved = localStorage.getItem('cck.lang');
    if (saved === 'fr' || saved === 'en') return saved;
  } catch {
    // storage unavailable
  }
  return navigator.language.toLowerCase().startsWith('fr') ? 'fr' : 'en';
})();

void i18next.use(initReactI18next).init({
  resources: { en: { translation: en }, fr: { translation: fr } },
  lng: initial,
  fallbackLng: 'fr',
  interpolation: { escapeValue: false },
  returnNull: false,
});
document.documentElement.lang = initial;

export function setLanguage(lang: Lang): void {
  if (i18next.language === lang) return;
  void i18next.changeLanguage(lang);
  document.documentElement.lang = lang;
  try {
    localStorage.setItem('cck.lang', lang);
  } catch {
    // storage unavailable
  }
}

/** t() and the current language, typed. */
export function useI18n() {
  const { t, i18n } = useTranslation();
  const lang: Lang = i18n.language === 'en' ? 'en' : 'fr';
  return { t, lang };
}

export default i18next;
